import { useEffect, useRef } from 'react';
import { formatRange, minutesToHHMM, type LessonDetail } from '@skolveckan/shared';
import { useApi } from '../hooks/useApi';
import { nowMinutes, useNow } from '../hooks/useNow';
import { api } from '../lib/api';
import { subjectColors } from '../lib/subjects';
import { IconClock, IconClose } from './Icons';
import { LessonChat } from './LessonChat';
import { LessonContent } from './LessonContent';

interface Props {
  lessonId: string | null;
  onClose: () => void;
}

/**
 * Byggd på <dialog> — fokus fångas, Escape stänger och bakgrunden blir inert
 * utan att vi skriver en egen fokusfälla.
 */
export function LessonModal({ lessonId, onClose }: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  const now = useNow(60_000);
  const nowMin = nowMinutes(now);

  const { data: lesson, loading, error } = useApi<LessonDetail | null>(
    () => (lessonId ? api.lesson(lessonId) : Promise.resolve(null)),
    [lessonId],
  );

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (lessonId && !dialog.open) dialog.showModal();
    if (!lessonId && dialog.open) dialog.close();
  }, [lessonId]);

  if (!lessonId) return null;

  const colors = subjectColors(lesson?.subject?.colorKey);
  const total = lesson ? Math.max(lesson.endMin - lesson.startMin, 1) : 1;
  const elapsed = lesson ? Math.min(Math.max(nowMin - lesson.startMin, 0), total) : 0;
  const isRunning = lesson ? nowMin >= lesson.startMin && nowMin < lesson.endMin : false;
  const left = total - elapsed;

  return (
    <dialog
      ref={ref}
      className="modal"
      onClose={onClose}
      onClick={(e) => {
        // Klick på bakgrunden (dialogens egen yta) stänger.
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="modal__inner">
        <header className="modal__head" style={{ background: colors.fill }}>
          <span className="modal__spine" style={{ background: colors.line }} />
          <div className="stack" style={{ gap: 3 }}>
            <h2 className="modal__subject" style={{ color: colors.ink }}>
              {lesson?.subject?.name ?? 'Lektion'}
            </h2>
            {lesson && (
              <span className="modal__meta" style={{ color: colors.ink }}>
                {formatRange(lesson.startMin, lesson.endMin)}
                {lesson.room ? ` · ${lesson.room}` : ''}
              </span>
            )}
          </div>
          <div className="grow" />
          {lesson && (
            <span className="modal__left">
              <IconClock size={15} />
              {isRunning
                ? `${left} min kvar`
                : nowMin < lesson.startMin
                  ? `Börjar ${minutesToHHMM(lesson.startMin)}`
                  : 'Avslutad'}
            </span>
          )}
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="Stäng lektionen"
          >
            <IconClose />
          </button>
        </header>

        <div className="modal__progress">
          <span
            style={{
              width: `${isRunning ? Math.round((elapsed / total) * 100) : 0}%`,
              background: colors.line,
            }}
          />
        </div>

        <div className="modal__body">
          {loading && <p className="muted">Hämtar lektionen…</p>}
          {error && <p className="muted">Lektionen kunde inte hämtas.</p>}
          {lesson && (
            <>
              <div className="modal__main">
                <LessonContent lesson={lesson} />
              </div>
              <aside className="modal__side">
                <LessonChat lessonId={lesson.id} enabled={lesson.chatEnabled} />
              </aside>
            </>
          )}
        </div>

        <footer className="modal__foot">
          {lesson?.assignments[0] && (
            <label className="modal__done">
              <input type="checkbox" defaultChecked={lesson.assignments[0].state === 'done'} />
              Markera lektionens uppgift som klar
            </label>
          )}
          <div className="grow" />
          {lesson?.next && (
            <span className="muted">
              Nästa: {lesson.next.subjectName} {minutesToHHMM(lesson.next.startMin)}
            </span>
          )}
          <button type="button" className="button button--ghost" onClick={onClose}>
            Stäng
          </button>
        </footer>
      </div>
    </dialog>
  );
}
